/**
 * ExoneracionModal - Componente reutilizable para agregar exoneraciones
 *
 * Usado en:
 * - CrearFactura.jsx
 * - CrearPlantillaRecurrenteModal.jsx
 * - FormularioCliente.jsx
 */

import { useState, useEffect } from 'react';
import { Shield, X, Search, Loader2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { receptoresApi, parametrosApi } from '../utils/api';
import useEscClose from '../hooks/useEscClose';

export default function ExoneracionModal({
  isOpen,
  onClose,
  onExoneracionSelected,
  clienteIdentificacion,
  clienteNombre = 'cliente',
  titulo = 'Agregar Exoneración',
  subtitulo = 'Solo para este documento',
  // initialData: si viene, modo EDICIÓN — pre-llena el formulario.
  // Formato esperado: { tipoDocumento, tipoDocumentoDesc, numeroDocumento,
  // nombreInstitucion (código), institucionNombre (desc), fechaEmision,
  // fechaVencimiento, porcentaje, cabys }
  initialData = null
}) {
  useEscClose(onClose);

  // Estados
  const [tiposExoneracion, setTiposExoneracion] = useState([]);
  const [institucionesExoneracion, setInstitucionesExoneracion] = useState([]);
  // Clasificaciones por Ley Especial (variantes del código '03': INCOPESCA, MAG, CONARE, etc.)
  const [clasificacionesLeyEspecial, setClasificacionesLeyEspecial] = useState([]);
  const [clasificacionSeleccionada, setClasificacionSeleccionada] = useState('');
  const [modoExoneracion, setModoExoneracion] = useState('hacienda');
  const [numeroExoneracionInput, setNumeroExoneracionInput] = useState('');
  const [consultandoExoneracion, setConsultandoExoneracion] = useState(false);
  const [tipoExoneracionSeleccionado, setTipoExoneracionSeleccionado] = useState('');
  const [formData, setFormData] = useState({
    tipoDocumento: '',
    tipoDocumentoDesc: '',
    numeroDocumento: '',
    nombreInstitucion: '',
    nombreInstitucionDesc: '',
    fechaEmision: '',
    fechaVencimiento: '',
    porcentaje: 13,
    // Articulo e inciso de la ley — usados especialmente para código '03' Ley Especial
    articuloLey: null,
    incisoLey: null
  });
  // Preservar los cabys de la exoneración cuando se edita (no se editan en este modal,
  // pero deben mantenerse para no perderlos al guardar).
  const [cabysExistentes, setCabysExistentes] = useState([]);
  // Modo edición (cuando viene initialData)
  const isEditMode = !!initialData;

  // Cargar tipos e instituciones
  useEffect(() => {
    if (!isOpen) return;

    const cargarDatos = async () => {
      try {
        const [tiposRes, institucionesRes, clasRes] = await Promise.all([
          parametrosApi.listTiposExoneracion(),
          parametrosApi.listInstitucionesExoneracion(),
          parametrosApi.listClasificacionesLeyEspecial().catch(() => ({ data: { data: [] } }))
        ]);
        setTiposExoneracion(tiposRes.data?.data || []);
        setInstitucionesExoneracion(institucionesRes.data?.data || []);
        setClasificacionesLeyEspecial(clasRes.data?.data || []);
      } catch (error) {
        console.error('Error cargando datos de exoneración:', error);
      }
    };
    cargarDatos();
  }, [isOpen]);

  // Aplicar una clasificación de Ley Especial (INCOPESCA / CONARE / etc) — pre-llena
  // institución, ley, artículo, inciso, tarifa exonerada y fecha de emisión de un solo click.
  const aplicarClasificacionLeyEspecial = (codigoClasif) => {
    setClasificacionSeleccionada(codigoClasif);
    if (!codigoClasif) return;
    const c = clasificacionesLeyEspecial.find(x => x.codigo === codigoClasif);
    if (!c) return;
    // Fecha de emisión por defecto: las clasificaciones actuales están basadas en
    // Ley 9635 "Fortalecimiento Finanzas Públicas" publicada en La Gaceta N°225,
    // Alcance Digital 202, el 04/12/2018. Si el cliente tiene un carné o autorización
    // individual (ej: carné INCOPESCA, registro D-140 MAG), el user puede sobreescribir
    // la fecha manualmente después.
    const fechaDefault = c.fechaEmisionDefecto || '2018-12-04';
    setFormData(prev => ({
      ...prev,
      // Forzamos tipo de exoneración '03 Autorizado por Ley Especial' por si aún no estaba seleccionado
      tipoDocumento: '03',
      tipoDocumentoDesc: 'Autorizado por Ley Especial',
      nombreInstitucion: c.institucionCodigo || prev.nombreInstitucion,
      nombreInstitucionDesc: c.institucionNombre || prev.nombreInstitucionDesc,
      numeroDocumento: c.numeroDocDefecto || prev.numeroDocumento,
      // Porcentaje: en este modal "porcentaje" se ha venido usando indistintamente —
      // para esta funcionalidad lo interpretamos como la tarifa exonerada (en puntos),
      // que es lo que va al campo XML tarifaexonerada del nodo Exoneración.
      porcentaje: c.tarifaExonerada,
      articuloLey: c.articuloLey,
      incisoLey: c.incisoLey,
      // Solo pre-llenar fecha si no había una (no sobrescribir lo del user al editar)
      fechaEmision: prev.fechaEmision || fechaDefault
    }));
    setTipoExoneracionSeleccionado('03');
  };

  // Pre-cargar datos al abrir en modo EDICIÓN — el modal por default crea nuevas,
  // pero si recibimos initialData (al editar una exoneración existente del receptor),
  // poblamos formData con esos valores y activamos modo manual.
  //
  // El backend solo guarda `institucionNombre` (texto), no el código por separado.
  // Por eso al editar buscamos el código en el catálogo de instituciones por match
  // del nombre. Si no encuentra match exacto, dejamos el código vacío pero
  // preservamos el nombre para que el user lo vea.
  useEffect(() => {
    if (!isOpen || !initialData) return;
    setModoExoneracion('manual');
    setTipoExoneracionSeleccionado(initialData.tipoDocumento || '');

    const nombreInstDesc = initialData.institucionNombre || initialData.nombreInstitucionDesc || '';
    // Buscar el código en el catálogo cargado (espera a que esté disponible)
    const instMatch = institucionesExoneracion.find(
      i => i.nombre === nombreInstDesc || nombreInstDesc.includes(i.nombre) || i.nombre?.includes(nombreInstDesc)
    );
    const codigoInst = initialData.institucionCodigo || initialData.nombreInstitucion || instMatch?.codigo || '';

    setFormData({
      tipoDocumento: initialData.tipoDocumento || '',
      tipoDocumentoDesc: initialData.tipoDocumentoDesc || '',
      numeroDocumento: initialData.numeroDocumento || '',
      nombreInstitucion: codigoInst,
      nombreInstitucionDesc: nombreInstDesc,
      fechaEmision: initialData.fechaEmision ? String(initialData.fechaEmision).slice(0, 10) : '',
      fechaVencimiento: initialData.fechaVencimiento ? String(initialData.fechaVencimiento).slice(0, 10) : '',
      porcentaje: initialData.porcentaje || 13
    });
    setCabysExistentes(Array.isArray(initialData.cabys) ? initialData.cabys : []);
  }, [isOpen, initialData, institucionesExoneracion]); // re-ejecuta cuando el catálogo termina de cargar

  // Reset al cerrar
  const resetModal = () => {
    setNumeroExoneracionInput('');
    setTipoExoneracionSeleccionado('');
    setModoExoneracion('hacienda');
    setCabysExistentes([]);
    setFormData({
      tipoDocumento: '',
      tipoDocumentoDesc: '',
      numeroDocumento: '',
      nombreInstitucion: '',
      nombreInstitucionDesc: '',
      fechaEmision: '',
      fechaVencimiento: '',
      porcentaje: 13
    });
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Formatear número de exoneración (AL-XXXXXXXX-XX)
  const formatearNumeroExoneracion = (value) => {
    let clean = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    clean = clean.slice(0, 12);
    let formatted = '';
    if (clean.length > 0) formatted = clean.slice(0, 2);
    if (clean.length > 2) formatted += '-' + clean.slice(2, 10);
    if (clean.length > 10) formatted += '-' + clean.slice(10, 12);
    return formatted;
  };

  const handleNumeroInputChange = (e) => {
    setNumeroExoneracionInput(formatearNumeroExoneracion(e.target.value));
  };

  // Consultar exoneración en Hacienda
  const consultarExoneracion = async () => {
    const numero = numeroExoneracionInput.trim();
    if (!numero) {
      toast.error('Ingrese el número de autorización');
      return;
    }

    if (!clienteIdentificacion) {
      toast.error('No hay cliente seleccionado para validar la exoneración');
      return;
    }

    setConsultandoExoneracion(true);
    try {
      const response = await receptoresApi.consultarExoneracion(numero);

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        // VALIDACIÓN CRÍTICA: Verificar que la exoneración pertenezca al cliente
        const identificacionExoneracion = data.identificacion?.replace(/\D/g, '');
        const identificacionCliente = clienteIdentificacion?.replace(/\D/g, '');

        if (identificacionExoneracion && identificacionCliente &&
            identificacionExoneracion !== identificacionCliente) {
          toast.error(
            `Esta exoneración pertenece a otra cédula (${data.identificacion}), no a ${clienteNombre} (${clienteIdentificacion})`,
            { duration: 6000 }
          );
          return;
        }

        if (!data.vigente) {
          toast.error('La exoneración está vencida');
          return;
        }

        // Exoneración válida
        const exoneracionData = {
          tipoDocumento: data.tipoDocumento?.codigo || '',
          tipoDocumentoDesc: data.tipoDocumento?.descripcion || '',
          numeroDocumento: data.numeroDocumento,
          nombreInstitucion: data.codigoInstitucion || '',
          nombreInstitucionDesc: data.nombreInstitucion || '',
          fechaEmision: data.fechaEmision,
          fechaVencimiento: data.fechaVencimiento,
          porcentaje: data.porcentajeExoneracion,
          cabys: data.cabys || []
        };

        onExoneracionSelected(exoneracionData);
        handleClose();
        toast.success('Exoneración válida y cargada correctamente');
      } else {
        toast.error(response.data?.error || 'No se encontró la exoneración');
      }
    } catch (error) {
      console.error('Error consultando exoneración:', error);
      toast.error(error.response?.data?.error || 'Error al consultar exoneración');
    } finally {
      setConsultandoExoneracion(false);
    }
  };

  // Seleccionar tipo predefinido
  const seleccionarTipo = (codigoTipo) => {
    setTipoExoneracionSeleccionado(codigoTipo);

    if (!codigoTipo) {
      setFormData({
        tipoDocumento: '',
        tipoDocumentoDesc: '',
        numeroDocumento: '',
        nombreInstitucion: '',
        nombreInstitucionDesc: '',
        fechaEmision: '',
        fechaVencimiento: '',
        porcentaje: 13
      });
      return;
    }

    const tipo = tiposExoneracion.find(t => t.codigo === codigoTipo);
    if (!tipo) return;

    setFormData({
      tipoDocumento: tipo.codigo,
      tipoDocumentoDesc: tipo.descripcion,
      numeroDocumento: tipo.numeroDocDefecto || '',
      nombreInstitucion: tipo.institucionCodigo || '',
      nombreInstitucionDesc: tipo.institucionNombre || '',
      fechaEmision: tipo.fechaEmisionDefecto || '',
      fechaVencimiento: '',
      porcentaje: tipo.porcentajeDefecto || 13
    });
  };

  // Cambiar institución
  const cambiarInstitucion = (codigoInstitucion) => {
    const inst = institucionesExoneracion.find(i => i.codigo === codigoInstitucion);
    if (inst) {
      setFormData(prev => ({
        ...prev,
        nombreInstitucion: inst.codigo,
        nombreInstitucionDesc: inst.nombre
      }));
    }
  };

  // Activar exoneración manual
  const activarExoneracionManual = () => {
    const tipo = tiposExoneracion.find(t => t.codigo === formData.tipoDocumento);

    if (!formData.tipoDocumento) {
      toast.error('Seleccione el tipo de exoneración');
      return;
    }

    if (tipo?.requiereNumeroDoc && !formData.numeroDocumento) {
      toast.error('Ingrese el número de documento');
      return;
    }

    if (tipo?.requiereFechas && !formData.fechaEmision) {
      toast.error('Ingrese la fecha de emisión');
      return;
    }

    if (!formData.porcentaje || formData.porcentaje <= 0) {
      toast.error('Ingrese el porcentaje de exoneración');
      return;
    }

    if (!formData.fechaEmision) {
      toast.error('La fecha de emisión es requerida');
      return;
    }

    const exoneracionData = {
      tipoDocumento: formData.tipoDocumento,
      tipoDocumentoDesc: formData.tipoDocumentoDesc,
      numeroDocumento: formData.numeroDocumento,
      nombreInstitucion: formData.nombreInstitucion,
      nombreInstitucionDesc: formData.nombreInstitucionDesc,
      fechaEmision: formData.fechaEmision,
      fechaVencimiento: formData.fechaVencimiento || null,
      porcentaje: formData.porcentaje,
      articuloLey: formData.articuloLey,
      incisoLey: formData.incisoLey,
      // Preservar cabys existentes al editar (este modal no permite editarlos en modo manual)
      cabys: cabysExistentes
    };

    onExoneracionSelected(exoneracionData);
    handleClose();
    toast.success(isEditMode ? 'Exoneración actualizada correctamente' : 'Exoneración configurada correctamente');
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleDateString('es-CR');
    } catch {
      return fecha;
    }
  };

  if (!isOpen) return null;

  const tipoSeleccionado = tiposExoneracion.find(t => t.codigo === tipoExoneracionSeleccionado);
  const tieneDefectos = tipoSeleccionado && tipoSeleccionado.numeroDocDefecto && tipoSeleccionado.fechaEmisionDefecto;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-purple-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-full">
              <Shield className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{titulo}</h2>
              <p className="text-sm text-gray-600">{subtitulo}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-purple-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Selector de modo */}
          <div className="flex gap-2 border-b border-purple-200 pb-3">
            <button
              type="button"
              onClick={() => setModoExoneracion('hacienda')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                modoExoneracion === 'hacienda'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-50'
              }`}
            >
              Consultar Hacienda (AL-)
            </button>
            <button
              type="button"
              onClick={() => setModoExoneracion('manual')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                modoExoneracion === 'manual'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-50'
              }`}
            >
              Ingresar Manualmente
            </button>
          </div>

          {modoExoneracion === 'hacienda' ? (
            /* Modo Hacienda */
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Número de Autorización (formato: AL-XXXXXXXX-XX)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={numeroExoneracionInput}
                  onChange={handleNumeroInputChange}
                  placeholder="AL-00000000-00"
                  maxLength={14}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono tracking-wider"
                />
                <button
                  type="button"
                  onClick={consultarExoneracion}
                  disabled={consultandoExoneracion || !numeroExoneracionInput.trim()}
                  className="px-4 py-2 border-2 border-purple-500 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                >
                  {consultandoExoneracion ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Consultando...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Consultar
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Consulta automática en Hacienda (para exoneraciones con número AL-)
              </p>
            </div>
          ) : (
            /* Modo Manual */
            <div className="space-y-4">
              {/* Selector de tipo */}
              <div>
                <label className="block text-xs text-gray-600 mb-1 font-medium">
                  Tipo de Exoneración *
                </label>
                <select
                  value={tipoExoneracionSeleccionado}
                  onChange={(e) => { seleccionarTipo(e.target.value); setClasificacionSeleccionada(''); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">Seleccionar tipo...</option>
                  {tiposExoneracion.map((tipo) => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      {tipo.codigo} - {tipo.descripcion}
                    </option>
                  ))}
                </select>
                {tipoSeleccionado?.notaAyuda && (
                  <p className="text-xs text-purple-600 mt-1">{tipoSeleccionado.notaAyuda}</p>
                )}
              </div>

              {/* Selector de clasificación — solo aparece cuando se elige tipo '03 Autorizado por Ley Especial'.
                  Permite seleccionar una variante pre-definida (INCOPESCA, MAG, CONARE, SINAES, Universidades, etc.)
                  que auto-llena institución, ley, artículo, inciso y tarifa exonerada. */}
              {tipoExoneracionSeleccionado === '03' && clasificacionesLeyEspecial.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                  <label className="block text-xs font-medium text-violet-900 mb-1.5 flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5" />
                    Clasificación específica (auto-llena los datos)
                  </label>
                  <select
                    value={clasificacionSeleccionada}
                    onChange={(e) => aplicarClasificacionLeyEspecial(e.target.value)}
                    className="w-full px-3 py-2 border border-violet-300 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white text-sm"
                  >
                    <option value="">— Seleccionar clasificación o llenar manualmente abajo —</option>
                    {clasificacionesLeyEspecial.map((c) => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.nombre} (tarifa efectiva {c.tarifaEfectiva}% → exonera {c.tarifaExonerada} puntos)
                      </option>
                    ))}
                  </select>
                  {clasificacionSeleccionada && (() => {
                    const c = clasificacionesLeyEspecial.find(x => x.codigo === clasificacionSeleccionada);
                    if (!c) return null;
                    return (
                      <div className="mt-2 text-[11px] text-violet-700 bg-white/60 rounded p-2 leading-relaxed">
                        <p className="font-semibold mb-0.5">{c.nombre}</p>
                        {c.descripcion && <p className="text-violet-600 mb-1">{c.descripcion}</p>}
                        <p>📋 <strong>{c.institucionNombre}</strong> · <strong>{c.numeroDocDefecto}</strong>
                          {c.articuloLey ? `, Art. ${c.articuloLey}` : ''}
                          {c.incisoLey ? `, Inciso ${c.incisoLey}` : ''}
                        </p>
                        {c.notaAyuda && <p className="text-[10px] text-amber-700 mt-1">⚠ {c.notaAyuda}</p>}
                      </div>
                    );
                  })()}
                </div>
              )}

              {tipoExoneracionSeleccionado && (
                <>
                  {/* Resumen de valores autocompletados */}
                  {tieneDefectos && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Datos autocompletados</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Documento:</span>
                          <span className="ml-1 font-medium">{formData.numeroDocumento}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Fecha:</span>
                          <span className="ml-1 font-medium">{formatearFecha(formData.fechaEmision)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Institución:</span>
                          <span className="ml-1 font-medium">{formData.nombreInstitucionDesc}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Porcentaje:</span>
                          <span className="ml-1 font-medium text-purple-700">{formData.porcentaje}%</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Campos editables si no hay valores por defecto */}
                  {!tieneDefectos && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Institución *</label>
                          <select
                            value={formData.nombreInstitucion}
                            onChange={(e) => cambiarInstitucion(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                          >
                            <option value="">Seleccionar...</option>
                            {institucionesExoneracion.map((inst) => (
                              <option key={inst.codigo} value={inst.codigo}>{inst.nombre}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Porcentaje *</label>
                          <div className="relative">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={formData.porcentaje || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, porcentaje: parseFloat(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 pr-8"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                          </div>
                        </div>
                      </div>

                      {tipoSeleccionado?.requiereNumeroDoc && (
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Número de Documento *</label>
                          <input
                            type="text"
                            value={formData.numeroDocumento || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, numeroDocumento: e.target.value }))}
                            placeholder="Número de resolución/autorización"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Fecha Emisión *</label>
                          <input
                            type="date"
                            value={formData.fechaEmision ? formData.fechaEmision.split('T')[0] : ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, fechaEmision: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Fecha Vencimiento</label>
                          <input
                            type="date"
                            value={formData.fechaVencimiento ? formData.fechaVencimiento.split('T')[0] : ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, fechaVencimiento: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Botón activar / actualizar */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={activarExoneracionManual}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      {isEditMode ? 'Actualizar Exoneración' : 'Activar Exoneración'}
                    </button>
                    {isEditMode && cabysExistentes.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-2 text-center">
                        Se preservarán los {cabysExistentes.length} código(s) CABYS asociados a esta exoneración.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
